// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api';
import type { UpdateCitecUserClientProfileRequestBody } from '@shared/data-access/interfaces/api/brokerage/client-types';
import { useCallback, useMemo, useState } from 'react';
import { mapClientProfileToDisplay } from '../../utils/map-client-profile-display';

/** Display shape shared by retail profile UI and advisor client profile. */
export type UserProfileData = {
  name: string;
  first_name: string;
  second_name: string | null;
  first_surname: string | null;
  second_surname: string | null;
  email: string;
  document_number: string;
  phone_number: string;
  address: string | null;
  postal_code: string | null;
  city: string | null;
  country: string | null;
  birth_date: string;
};

export const useUserProfile = () => {
  const { data: profileData, isLoading } =
    advisoryApi.useGetCitecUserClientProfileQuery();

  const [updateProfile, { isLoading: isUpdating }] =
    advisoryApi.useUpdateCitecUserClientProfileMutation();

  const [openEdit, setOpenEdit] = useState(false);

  const profile = useMemo<UserProfileData | null>(() => {
    if (!profileData) return null;

    return mapClientProfileToDisplay(profileData);
  }, [profileData]);

  const fullName = useMemo(() => profile?.name || '-', [profile]);

  const fullAddress = useMemo(() => {
    const parts = [profile?.address, profile?.postal_code, profile?.city].filter(
      v => v && v !== null,
    );
    return parts.length ? parts.join(', ') : '-';
  }, [profile]);

  const handleSave = useCallback(
    async (data: UserProfileData) => {
      /* istanbul ignore next */
      const updatePayload: UpdateCitecUserClientProfileRequestBody = {
        first_name: data.first_name || undefined,
        second_name: data.second_name || undefined,
        first_surname: data.first_surname || undefined,
        second_surname: data.second_surname || undefined,
        street_name: data.address || undefined,
        postal_code: data.postal_code || undefined,
        city: data.city || undefined,
        country: data.country || undefined,
        birth_date: data.birth_date || undefined,
        document_number:
          data.document_number !== '-' ? data.document_number : undefined,
        phone_number: data.phone_number || undefined,
      };

      const cleanPayload = Object.fromEntries(
        Object.entries(updatePayload).filter(
          ([_, value]) => value !== null && value !== '-',
        ),
      ) as UpdateCitecUserClientProfileRequestBody;

      await updateProfile(cleanPayload).unwrap();
    },
    [updateProfile],
  );

  const onOpenEdit = useCallback(() => {
    setOpenEdit(true);
  }, []);

  const onCloseEdit = useCallback(() => {
    setOpenEdit(false);
  }, []);

  return {
    profile,
    isLoading,
    openEdit,
    setOpenEdit,
    onOpenEdit,
    onCloseEdit,
    handleSave,
    isUpdating,
    fullName,
    fullAddress,
  };
};
